import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import SitePage, { calendly, styles, T, ACCENT } from '../../components/site/SitePage';
import '../outils/outils.css';
import './diagnostic.css';

// Questionnaire « décrivez votre besoin en 1 minute » : 5 questions à choix, puis l'e-mail.
// Les réponses partent par /api/diagnostic (worker/index.js), qui les envoie à hello@autoria.dev.

export const QUESTIONS = [
  { id: 'equipe', titre: "Quelle équipe a le plus besoin d'un outil ?", choix: [
    ['stock', 'Achats et stock'], ['finance', 'Finance et marge'], ['marketing', 'Marketing et publicité'],
    ['catalogue', 'Catalogue et opérations'], ['ne-sait-pas', 'Je ne sais pas encore']] },
  { id: 'donnees', titre: 'Où sont vos données aujourd’hui ?', choix: [
    ['excel', 'Dans Excel ou Google Sheets'], ['exports', 'Des exports Amazon ou Shopify recopiés à la main'],
    ['logiciel', 'Dans un logiciel qui ne suffit plus'], ['partout', 'Un peu partout']] },
  { id: 'canaux', titre: 'Sur quels canaux vendez-vous ?', multiple: true, choix: [
    ['amazon', 'Amazon'], ['shopify', 'Shopify'], ['marketplaces', 'Autres marketplaces'],
    ['b2b', 'B2B et revendeurs'], ['boutiques', 'Boutiques physiques']] },
  { id: 'ca', titre: "Votre chiffre d'affaires annuel ?", choix: [
    ['moins-1', 'Moins de 1 M€'], ['1-5', '1 à 5 M€'], ['5-20', '5 à 20 M€'], ['plus-20', 'Plus de 20 M€']] },
  { id: 'quand', titre: 'Quand voulez-vous avancer ?', choix: [
    ['mois', 'Ce mois-ci'], ['trimestre', 'Ce trimestre'], ['renseigne', 'Je me renseigne']] }
];

const OUTILS = {
  stock: ["Un outil de réappro", "Quoi commander, combien et quand, sur tous vos canaux, à partir de vos ventes, de votre stock et de vos délais fournisseurs.", '/reappro-prevision-stock', "Voir l'outil de réappro"],
  finance: ['La marge réelle, dans une seule base', 'Une marge par produit et par canal, frais Amazon, publicité et retours déduits, rapprochée de vos virements.', '/marge-par-canal', "Voir l'outil de marge"],
  marketing: ['La publicité pilotée sur la marge', 'Ce que coûte chaque campagne, ce qu’elle rapporte vraiment, et lesquelles couper.', '/outils/calcul-marge-amazon', 'Calculer mon seuil de publicité'],
  catalogue: ['Un catalogue unique, pour tous vos canaux', 'Une fiche par produit, la même sur tous vos canaux : un prix change une fois, il change partout.', '/#equipes', 'Voir la démo'],
  'ne-sait-pas': ['Un appel pour trouver le bon point de départ', 'En 30 minutes, on repère le fichier qui vous coûte le plus de temps : c’est souvent par lui qu’il faut commencer.', '/#equipes', 'Voir les démos']
};

// Canaux cités dans la recommandation, dans une phrase (« Amazon, Shopify et vos revendeurs »).
const CANAL = { amazon: 'Amazon', shopify: 'Shopify', marketplaces: 'vos autres marketplaces', b2b: 'vos revendeurs', boutiques: 'vos boutiques' };
const joint = (l) => (l.length > 1 ? `${l.slice(0, -1).join(', ')} et ${l[l.length - 1]}` : l[0]);
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);

const label = (q, v) => (QUESTIONS.find((x) => x.id === q).choix.find((c) => c[0] === v) || [, v])[1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Check = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
);

const Progress = ({ step, total }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>{step < total ? `Question ${step + 1} sur ${total}` : 'Dernière étape'}</span>
    <div style={{ height: 4, background: T.line, borderRadius: 2, overflow: 'hidden' }} aria-hidden="true">
      <div style={{ height: '100%', width: `${(Math.min(step, total) / total) * 100}%`, background: ACCENT, transition: 'width .3s ease' }} />
    </div>
  </div>
);

const Result = ({ a, contact, sent }) => {
  const [titre, texte, href, lien] = OUTILS[a.equipe] || OUTILS['ne-sait-pas'];
  const petit = a.ca === 'moins-1';
  const canaux = (a.canaux || []).map((v) => CANAL[v]).filter(Boolean);
  const resume = QUESTIONS.map((q) => (q.multiple ? (a[q.id] || []).map((v) => label(q.id, v)).join(', ') : label(q.id, a[q.id]))).join(' · ');
  const params = new URLSearchParams({ name: contact.prenom || '', email: contact.email, a1: resume });
  const cal = `${calendly('diagnostic')}&${params.toString()}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <p style={{ ...styles.kicker, color: ACCENT }}>{sent ? 'Réponses envoyées' : 'Votre recommandation'}</p>
      <h2 style={{ ...styles.h1, fontSize: 'clamp(32px, 4vw, 48px)' }}>{titre}</h2>
      <p style={{ ...styles.lead, maxWidth: 640 }}>{texte}</p>
      <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 17, color: T.text, lineHeight: 1.55, maxWidth: 640 }}>
        {(a.donnees === 'excel' || a.donnees === 'partout') && <li>On part de vos fichiers tels qu’ils sont : rien ne change pour l’équipe le premier jour.</li>}
        {a.donnees === 'exports' && <li>Les exports recopiés à la main disparaissent : Amazon et Shopify se connectent directement à la base.</li>}
        {a.donnees === 'logiciel' && <li>On garde votre logiciel : la base se nourrit de ses données, et l’outil couvre ce qu’il ne fait pas.</li>}
        {canaux.length > 1 && <li>{cap(joint(canaux))} arrivent dans la même base{a.equipe === 'stock' && (a.canaux || []).includes('amazon') ? ' : la prévision les additionne, et compte le stock déjà chez Amazon.' : ' : un seul chiffre par produit, tous canaux confondus.'}</li>}
        {(a.ca === '5-20' || a.ca === 'plus-20') && <li>À votre taille, le plus sûr est de démarrer par un outil, puis d’étendre la même base aux autres équipes.</li>}
        {petit
          ? <li>À votre taille, un Excel bien construit suffit peut-être encore. Commencez par le <Link to="/outils/calcul-stock-de-securite">calculateur gratuit</Link>, et parlons-en si le fichier ne suit plus.</li>
          : <li>Pour commencer : un premier outil sur mesure, dès 2 900 € HT, livré en 3 semaines sur vos vraies données. Tarif de lancement : 2 030 € HT au lieu de 2 900 € pour un premier outil.</li>}
        <li>{sent ? 'Je vous réponds sous 24 h ouvrées à ' : 'Votre adresse : '}<strong>{contact.email}</strong>.</li>
      </ul>
      {!sent && (
        <p role="alert" style={{ margin: 0, padding: '12px 14px', background: '#FDF1EA', border: '1px solid #F2C6AE', borderRadius: 6, fontSize: 15, color: '#7C2D12', maxWidth: 640 }}>
          L’envoi n’a pas abouti. Écrivez-moi directement à <a href="mailto:hello@autoria.dev">hello@autoria.dev</a>, ou réservez un appel ci-dessous.
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <a href={cal} target="_blank" rel="noopener" className="lift" style={styles.button}>{a.quand === 'renseigne' ? 'Réserver un appel quand vous voulez' : 'Réserver 30 minutes'}</a>
        <Link to={href} className="lift" style={{ ...styles.button, background: '#FFFFFF', color: T.ink, border: `1px solid ${T.ink}`, fontWeight: 600 }}>{lien}</Link>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: T.muted }}>Le formulaire de rendez-vous reprend déjà votre nom, votre adresse et vos réponses.</p>
    </div>
  );
};

const Diagnostic = () => {
  const [step, setStep] = useState(0);
  const [a, setA] = useState({ canaux: [] });
  const [contact, setContact] = useState({ email: '', prenom: '', entreprise: '', message: '', site: '' });
  const [state, setState] = useState('idle'); // idle | sending | sent | failed
  const [error, setError] = useState('');
  const started = useRef(0);
  const top = useRef(null);

  const begun = useRef(false);

  useEffect(() => { started.current = Date.now(); }, []);
  useEffect(() => { if (step > 0 && top.current) top.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [step]);

  const q = QUESTIONS[step];
  const pick = (v) => {
    if (step === 0 && !begun.current) {
      begun.current = true;
      if (window.autoriaTrack) window.autoriaTrack('questionnaire_commence', { equipe: v });
    }
    if (q.multiple) {
      setA((prev) => ({ ...prev, [q.id]: prev[q.id].includes(v) ? prev[q.id].filter((x) => x !== v) : [...prev[q.id], v] }));
      return;
    }
    setA((prev) => ({ ...prev, [q.id]: v }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(contact.email.trim())) { setError('Indiquez une adresse e-mail valide.'); return; }
    setError('');
    setState('sending');
    const payload = {
      reponses: Object.fromEntries(QUESTIONS.map((x) => [x.titre, x.multiple ? (a[x.id] || []).map((v) => label(x.id, v)).join(', ') || '—' : label(x.id, a[x.id])])),
      ...contact, email: contact.email.trim(),
      duree: Date.now() - started.current,
      provenance: document.referrer || 'direct'
    };
    try {
      const r = await fetch('/api/diagnostic', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error(String(r.status));
      setState('sent');
      if (window.autoriaTrack) window.autoriaTrack('questionnaire_envoye', { equipe: a.equipe, ca: a.ca, quand: a.quand });
    } catch {
      setState('failed');
    }
    setStep(QUESTIONS.length + 1);
  };

  const card = { background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 12, padding: 'clamp(22px, 4vw, 40px)', boxShadow: '0 30px 60px -40px rgba(21,23,26,.35)' };

  if (step > QUESTIONS.length) {
    return <div ref={top} style={card}><Result a={a} contact={contact} sent={state === 'sent'} /></div>;
  }

  return (
    <div ref={top} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 26 }}>
      <Progress step={step} total={QUESTIONS.length} />
      {q ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <h2 style={{ ...styles.h2, fontSize: 'clamp(24px, 3vw, 32px)' }}>{q.titre}</h2>
          {q.multiple && <p style={{ margin: '-8px 0 0', fontSize: 15, color: T.muted }}>Plusieurs réponses possibles.</p>}
          <div role="group" aria-label={q.titre} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 10 }}>
            {q.choix.map(([v, l]) => {
              const on = q.multiple ? a[q.id].includes(v) : a[q.id] === v;
              return (
                <button key={v} type="button" className="dg-choice" aria-pressed={on} onClick={() => pick(v)}>
                  <span className="dg-box" style={{ borderRadius: q.multiple ? 4 : '50%' }}>{on && <Check />}</span>{l}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, minHeight: 46 }}>
            {step > 0 ? <button type="button" className="dg-back" onClick={() => setStep(step - 1)}>← Retour</button> : <span />}
            {q.multiple && (
              <button type="button" disabled={!a[q.id].length} onClick={() => setStep(step + 1)} className="lift" style={{ ...styles.button, border: 0, cursor: a[q.id].length ? 'pointer' : 'not-allowed', opacity: a[q.id].length ? 1 : 0.4, minHeight: 46, padding: '10px 20px', fontSize: 16 }}>Continuer</button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <h2 style={{ ...styles.h2, fontSize: 'clamp(24px, 3vw, 32px)' }}>Où vous envoyer la recommandation ?</h2>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, fontWeight: 600 }}>
            <span>En une phrase, qu’est-ce qui vous fait perdre le plus de temps ? <span style={{ fontWeight: 400, color: T.muted }}>· facultatif</span></span>
            <textarea className="ou-input" rows={3} maxLength={1500} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
              style={{ height: 'auto', padding: 12, font: `400 16px/1.5 ${T.sans}`, resize: 'vertical' }} placeholder="Par exemple : on refait le fichier de réappro à la main chaque lundi." />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14, alignItems: 'end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, fontWeight: 600 }}>
              <span>E-mail professionnel</span>
              <input className="ou-input" type="email" autoComplete="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} style={{ fontFamily: T.sans, fontSize: 16 }} aria-invalid={!!error} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, fontWeight: 600 }}>
              <span>Prénom <span style={{ fontWeight: 400, color: T.muted }}>· facultatif</span></span>
              <input className="ou-input" autoComplete="given-name" maxLength={100} value={contact.prenom} onChange={(e) => setContact({ ...contact, prenom: e.target.value })} style={{ fontFamily: T.sans, fontSize: 16 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, fontWeight: 600 }}>
              <span>Entreprise <span style={{ fontWeight: 400, color: T.muted }}>· facultatif</span></span>
              <input className="ou-input" autoComplete="organization" maxLength={100} value={contact.entreprise} onChange={(e) => setContact({ ...contact, entreprise: e.target.value })} style={{ fontFamily: T.sans, fontSize: 16 }} />
            </label>
          </div>
          {/* Champ piège : invisible pour un humain, rempli par les robots. */}
          <label className="dg-hp" aria-hidden="true">Site web<input tabIndex={-1} autoComplete="off" value={contact.site} onChange={(e) => setContact({ ...contact, site: e.target.value })} /></label>
          {error && <p role="alert" style={{ margin: 0, color: '#C2410C', fontSize: 15 }}>{error}</p>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" className="dg-back" onClick={() => setStep(step - 1)}>← Retour</button>
            <button type="submit" disabled={state === 'sending'} className="lift" style={{ ...styles.button, border: 0, cursor: 'pointer' }}>{state === 'sending' ? 'Envoi…' : 'Voir ma recommandation'}</button>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: T.muted }}>Vos réponses servent uniquement à vous répondre. Rien n’est revendu ni partagé. <Link to="/mentions-legales">En savoir plus</Link></p>
        </form>
      )}
    </div>
  );
};

const DiagnosticPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <SitePage>
      <SEO
        title="Décrivez votre besoin en 1 minute"
        description="Cinq questions pour savoir quel outil sur mesure aiderait vos équipes e-commerce : réappro, marge, publicité ou catalogue. Gratuit, réponse par e-mail."
        path="/diagnostic"
        breadcrumb={[['Questionnaire', '/diagnostic']]}
      />
      <section style={{ ...styles.narrow, maxWidth: 860, padding: '48px 24px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={styles.kicker}>Questionnaire · 1 minute</p>
          <h1 style={{ ...styles.h1, fontSize: 'clamp(32px, 4.2vw, 52px)' }}>Décrivez votre besoin en 1 minute</h1>
          <p style={{ ...styles.lead, maxWidth: 640 }}>Cinq questions, et vous savez par quel outil commencer. Je vous réponds aussi personnellement par e-mail.</p>
        </div>
        <Diagnostic />
      </section>
    </SitePage>
  );
};

export default DiagnosticPage;
